import React from "react";
import { formatDateShort } from "../../utils/formatters";
import { MSG_PAGE_SIZE, type PreviewData } from "../../hooks/data-fetching/useConversationPreviews";

// =============================================================================
// PreviewCard — the condensed neighbor card that flanks the reader (dm-swipe-nav
// F3). ONE render path for every DM surface (CLAUDE.md #10): each surface adapts
// its list row into PreviewData, this component draws it. A field still null
// renders a shimmer (data-testid dm-preview-skeleton), never a blank. A neighbor
// row with no DM thread (P6 orphan/lost with a null conversation_id) renders the
// empty state (data-testid dm-preview-empty).
//
// Card content is pinned from the approved mockup (scratchpad/dm-reader-swipe.html)
// so this component does not depend on that file.
// =============================================================================

interface PreviewCardProps {
  /** The neighbor's data. `null` = there is no neighbor in that direction (an
   *  end of the list) — renders an invisible spacer to keep the reader centered. */
  data: PreviewData | null;
  direction: "prev" | "next";
  /** dm-preview-prev | dm-preview-next (the harness queries these). */
  testId: string;
  onClick?: () => void;
  /** A neighbor row that exists but has no DM thread (null conversation_id). */
  empty?: boolean;
}

const SHIMMER = "inline-block h-3 w-10 rounded bg-black/[0.08] animate-pulse align-middle";

function Shimmer({ w = "w-10" }: { w?: string }) {
  return <span data-testid="dm-preview-skeleton" className={`${SHIMMER} ${w}`} />;
}

const snippet = (text: string | null): string => {
  if (!text) return "";
  return text.length > 72 ? `${text.slice(0, 71)}…` : text;
};

const whoLastLabel = (who: PreviewData["whoLast"]): string =>
  who === "you" ? "you spoke last" : who === "lead" ? "lead spoke last" : "automated";

export const PreviewCard: React.FC<PreviewCardProps> = ({
  data,
  direction,
  testId,
  onClick,
  empty,
}) => {
  const dirLabel = direction === "prev" ? "◄ PREVIOUS" : "NEXT ►";
  const cardBase =
    "hidden xl:flex w-[190px] flex-shrink-0 flex-col gap-2 rounded-xl p-3 detail-overlay text-left transition-colors";

  // No neighbor in this direction (an end) — invisible spacer holds the layout.
  if (!data && !empty) {
    return <div className={cardBase} style={{ visibility: "hidden" }} aria-hidden="true" />;
  }

  // A neighbor row with no DM thread (P6).
  if (empty || !data) {
    return (
      <div className={`${cardBase} items-center justify-center`} data-testid="dm-preview-empty">
        <p className="token-11 text-glass-muted uppercase tracking-wide">{dirLabel}</p>
        <p className="token-12 text-glass-muted text-center">no DM thread</p>
      </div>
    );
  }

  const msgLabel =
    data.messageCount == null
      ? null
      : data.messageCount >= MSG_PAGE_SIZE
        ? `${data.messageCount}+`
        : `${data.messageCount}`;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`${cardBase} hover:bg-black/[0.04] cursor-pointer`}
    >
      <p className="token-10 font-semibold text-glass-muted uppercase tracking-wide">{dirLabel}</p>

      {/* identity — always known from the sibling row */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center text-glass token-11 font-bold flex-shrink-0">
          {data.contactName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="token-12 font-medium text-glass truncate">{data.contactName}</p>
          {data.leadInstagram && (
            <p className="token-10 text-glass-muted truncate">@{data.leadInstagram}</p>
          )}
        </div>
      </div>

      {/* last-message snippet */}
      <p className="token-11 italic text-glass-secondary line-clamp-2 min-h-[1.5em]">
        {data.lastMessageText == null ? <Shimmer w="w-24" /> : snippet(data.lastMessageText)}
      </p>

      {/* 3-cell metric strip */}
      <div className="flex items-center justify-between gap-1 token-10 text-glass-muted">
        <span>{msgLabel == null ? <Shimmer w="w-6" /> : `${msgLabel} msgs`}</span>
        <span>{data.replyTurns == null ? <Shimmer w="w-6" /> : `${data.replyTurns} turns`}</span>
        <span className="text-ds-red">
          {data.waitLabel == null ? "—" : `${data.waitLabel} wait`}
        </span>
      </div>

      {/* who-last + last-message date */}
      <p className="token-10 text-glass-muted truncate">
        {data.whoLast == null ? (
          <Shimmer w="w-20" />
        ) : (
          <>
            {whoLastLabel(data.whoLast)}
            {data.lastMessageAt && ` · ${formatDateShort(new Date(data.lastMessageAt))}`}
          </>
        )}
      </p>

      {/* setter */}
      <p className="token-10 text-glass-muted truncate">
        Setter: {data.setterName == null ? <Shimmer w="w-14" /> : data.setterName}
      </p>
    </button>
  );
};
